import { AxiosResponse } from 'axios';
import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';
import { mapFxTransaction } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import {
  asResponse,
  paginationRange,
  buildPaginatedResponse,
  throwIfError,
} from '@/lib/supabase-helpers';
import { ResponseWithPagination } from '@/types';
import { FxTransaction, FxTransactionType } from '@/types/api/finance';

const SELECT_WITH_JOINS = '*, fx_vendors(*, fx_categories(*)), fx_categories(*)';

const financeTransactionsService = {
  async getAll({
    page,
    take,
    vendorId,
    categoryId,
    type,
    source,
    importId,
    startDate,
    endDate,
  }: {
    page: number;
    take: number;
    vendorId?: string;
    categoryId?: string;
    type?: FxTransactionType;
    source?: string;
    importId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<ResponseWithPagination<FxTransaction[]>>> {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('fx_transactions')
      .select(SELECT_WITH_JOINS, { count: 'exact' })
      .order('date', { ascending: false })
      .range(from, to);

    query = applyOrgFilter(query);
    if (vendorId) query = query.eq('vendor_id', vendorId);
    if (categoryId) query = query.eq('category_id', categoryId);
    if (type) query = query.eq('type', type);
    if (source) query = query.eq('source', source);
    if (importId) query = query.eq('import_id', importId);
    if (startDate) query = query.gte('date', startDate);
    if (endDate) query = query.lte('date', endDate);

    const { data, error, count } = await query;
    if (error) throw error;
    return asResponse(
      buildPaginatedResponse((data ?? []).map(mapFxTransaction), count ?? 0, page, take)
    );
  },

  async getById(id: string): Promise<AxiosResponse<FxTransaction>> {
    let query = supabase
      .from('fx_transactions')
      .select(SELECT_WITH_JOINS)
      .eq('id', id);
    query = applyOrgFilter(query);
    const { data, error } = await query.single();

    if (error) throw error;
    return asResponse(mapFxTransaction(data));
  },

  async create(payload: {
    date: string;
    description: string;
    amount: number;
    type?: FxTransactionType;
    currency?: string;
    vendorId?: string;
    categoryId?: string;
    subscriptionId?: string;
    notes?: string;
  }): Promise<AxiosResponse<FxTransaction>> {
    const orgId = getActiveOrgId();
    const insert: Record<string, unknown> = {
      date: payload.date,
      description: payload.description,
      amount: payload.amount,
      source: 'manual',
      ...(orgId ? { organization_id: orgId } : {}),
    };
    if (payload.type !== undefined) insert.type = payload.type;
    if (payload.currency !== undefined) insert.currency = payload.currency;
    if (payload.vendorId !== undefined) insert.vendor_id = payload.vendorId;
    if (payload.categoryId !== undefined) insert.category_id = payload.categoryId;
    if (payload.subscriptionId !== undefined) insert.subscription_id = payload.subscriptionId;
    if (payload.notes !== undefined) insert.notes = payload.notes;

    const result = await supabase
      .from('fx_transactions')
      .insert(insert)
      .select(SELECT_WITH_JOINS)
      .single();

    return asResponse(mapFxTransaction(throwIfError(result)));
  },

  async bulkCreate(
    importId: string,
    transactions: Array<{
      date: string;
      description: string;
      amount: number;
      type: FxTransactionType;
      currency?: string;
      vendorId?: string | null;
      categoryId?: string | null;
      aiConfidence?: number | null;
      aiSuggested?: boolean;
      userVerified?: boolean;
      subscriptionId?: string | null;
      matchSource?: string | null;
      matchedRuleId?: string | null;
      notes?: string;
    }>
  ): Promise<AxiosResponse<FxTransaction[]>> {
    const orgId = getActiveOrgId();
    const rows = transactions.map((t) => ({
      date: t.date,
      description: t.description,
      amount: t.amount,
      type: t.type,
      currency: t.currency ?? 'USD',
      vendor_id: t.vendorId ?? null,
      category_id: t.categoryId ?? null,
      subscription_id: t.subscriptionId ?? null,
      import_id: importId,
      source: 'import' as const,
      ai_confidence: t.aiConfidence ?? null,
      ai_suggested: t.aiSuggested ?? false,
      user_verified: t.userVerified ?? false,
      notes: t.notes ?? null,
      ...(orgId ? { organization_id: orgId } : {}),
    }));

    const { data, error } = await supabase
      .from('fx_transactions')
      .insert(rows)
      .select(SELECT_WITH_JOINS);

    if (error) throw error;
    return asResponse((data ?? []).map(mapFxTransaction));
  },

  async update(
    id: string,
    payload: {
      date?: string;
      description?: string;
      amount?: number;
      type?: FxTransactionType;
      currency?: string;
      vendorId?: string | null;
      categoryId?: string | null;
      subscriptionId?: string | null;
      userVerified?: boolean;
      reviewStatus?: string;
      notes?: string;
    }
  ): Promise<AxiosResponse<FxTransaction>> {
    const updates: Record<string, unknown> = {};
    if (payload.date !== undefined) updates.date = payload.date;
    if (payload.description !== undefined) updates.description = payload.description;
    if (payload.amount !== undefined) updates.amount = payload.amount;
    if (payload.type !== undefined) updates.type = payload.type;
    if (payload.currency !== undefined) updates.currency = payload.currency;
    if (payload.vendorId !== undefined) updates.vendor_id = payload.vendorId;
    if (payload.categoryId !== undefined) updates.category_id = payload.categoryId;
    if (payload.subscriptionId !== undefined) updates.subscription_id = payload.subscriptionId;
    if (payload.userVerified !== undefined) updates.user_verified = payload.userVerified;
    if (payload.reviewStatus !== undefined) updates.review_status = payload.reviewStatus;
    if (payload.notes !== undefined) updates.notes = payload.notes;

    const result = await supabase
      .from('fx_transactions')
      .update(updates)
      .eq('id', id)
      .select(SELECT_WITH_JOINS)
      .single();

    return asResponse(mapFxTransaction(throwIfError(result)));
  },

  async delete(id: string): Promise<AxiosResponse<void>> {
    const { error } = await supabase.from('fx_transactions').delete().eq('id', id);

    if (error) throw error;
    return asResponse(undefined as void);
  },

  async bulkDelete(ids: string[]): Promise<void> {
    const { error } = await supabase.from('fx_transactions').delete().in('id', ids);

    if (error) throw error;
  },

  async getByDateRange(
    startDate: string,
    endDate: string
  ): Promise<
    AxiosResponse<Array<{ id: string; date: string; description: string; amount: number }>>
  > {
    let query = supabase
      .from('fx_transactions')
      .select('id, date, description, amount')
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return asResponse(
      (data ?? []).map((r: any) => ({
        id: r.id,
        date: r.date,
        description: r.description,
        amount: parseFloat(r.amount),
      }))
    );
  },

  async getMonthlyTotals({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<AxiosResponse<Array<{ month: string; revenue: number; expenses: number }>>> {
    let query = supabase
      .from('fx_transactions')
      .select('date, type, amount, fx_categories(type)')
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    const grouped: Record<string, { revenue: number; expenses: number }> = {};
    (data ?? []).forEach((row: any) => {
      if (row.fx_categories?.type === 'transfer') return;
      const month = row.date.substring(0, 7); // YYYY-MM
      if (!grouped[month]) grouped[month] = { revenue: 0, expenses: 0 };
      const amount = parseFloat(row.amount);
      if (row.type === 'income') {
        grouped[month].revenue += amount;
      } else {
        grouped[month].expenses += amount;
      }
    });

    // Generate all months between startDate and endDate to ensure continuous series
    const allMonths: string[] = [];
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);
    let y = startYear;
    let m = startMonth;
    while (y < endYear || (y === endYear && m <= endMonth)) {
      allMonths.push(`${y}-${String(m).padStart(2, '0')}`);
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }

    // Build result with zero-filled months for any gaps
    const result = allMonths.map((month) => ({
      month,
      revenue: Math.round((grouped[month]?.revenue ?? 0) * 100) / 100,
      expenses: Math.round((grouped[month]?.expenses ?? 0) * 100) / 100,
    }));

    return asResponse(result);
  },

  async getTotalsByType({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<AxiosResponse<{ revenue: number; expenses: number }>> {
    let query = supabase
      .from('fx_transactions')
      .select('type, amount, fx_categories(type)')
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    let revenue = 0;
    let expenses = 0;
    (data ?? []).forEach((row: any) => {
      // Exclude transfer categories (credit card payments, internal transfers)
      if (row.fx_categories?.type === 'transfer') return;
      const amount = parseFloat(row.amount);
      if (row.type === 'income') revenue += amount;
      else expenses += amount;
    });

    return asResponse({
      revenue: Math.round(revenue * 100) / 100,
      expenses: Math.round(expenses * 100) / 100,
    });
  },

  async getSpendByCategory({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<AxiosResponse<Array<{ categoryId: string; categoryName: string; total: number }>>> {
    let query = supabase
      .from('fx_transactions')
      .select('category_id, fx_categories(name, type), amount')
      .eq('type', 'expense')
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    const grouped: Record<string, { categoryId: string; categoryName: string; total: number }> = {};
    (data ?? []).forEach((row: any) => {
      // Exclude transfer categories (credit card payments, internal transfers)
      if (row.fx_categories?.type === 'transfer') return;
      const catId = row.category_id ?? 'uncategorized';
      const catName = row.fx_categories?.name ?? 'Uncategorized';
      if (!grouped[catId]) {
        grouped[catId] = { categoryId: catId, categoryName: catName, total: 0 };
      }
      grouped[catId].total += parseFloat(row.amount);
    });

    return asResponse(Object.values(grouped));
  },

  async getTopVendors({
    startDate,
    endDate,
    limit = 10,
  }: {
    startDate: string;
    endDate: string;
    limit?: number;
  }): Promise<
    AxiosResponse<Array<{ vendorId: string; vendorName: string; total: number; txCount: number }>>
  > {
    let query = supabase
      .from('fx_transactions')
      .select('vendor_id, fx_vendors(name, display_name), fx_categories(type), amount')
      .eq('type', 'expense')
      .not('vendor_id', 'is', null)
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    const grouped: Record<
      string,
      { vendorId: string; vendorName: string; total: number; txCount: number }
    > = {};
    (data ?? []).forEach((row: any) => {
      // Exclude transfer categories
      if (row.fx_categories?.type === 'transfer') return;
      const vId = row.vendor_id;
      if (!grouped[vId]) {
        grouped[vId] = {
          vendorId: vId,
          vendorName: row.fx_vendors?.display_name || row.fx_vendors?.name || 'Unknown',
          total: 0,
          txCount: 0,
        };
      }
      grouped[vId].total += parseFloat(row.amount);
      grouped[vId].txCount += 1;
    });

    const sorted = Object.values(grouped)
      .sort((a, b) => b.total - a.total)
      .slice(0, limit);

    return asResponse(sorted);
  },

  async getMonthlyBurn({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<AxiosResponse<Array<{ month: string; burn: number; cumulative: number }>>> {
    let query = supabase
      .from('fx_transactions')
      .select('date, type, amount, fx_categories(type)')
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    // Net burn per month = expenses - revenue (excluding transfers)
    const grouped: Record<string, { expenses: number; revenue: number }> = {};
    (data ?? []).forEach((row: any) => {
      if (row.fx_categories?.type === 'transfer') return;
      const month = row.date.substring(0, 7);
      if (!grouped[month]) grouped[month] = { expenses: 0, revenue: 0 };
      const amount = parseFloat(row.amount);
      if (row.type === 'income') grouped[month].revenue += amount;
      else grouped[month].expenses += amount;
    });

    let cumulative = 0;
    const result = Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, totals]) => {
        const burn = Math.round((totals.expenses - totals.revenue) * 100) / 100;
        cumulative = Math.round((cumulative + burn) * 100) / 100;
        return { month, burn, cumulative };
      });

    return asResponse(result);
  },

  async getUncategorizedCount({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<AxiosResponse<number>> {
    let query = supabase
      .from('fx_transactions')
      .select('id', { count: 'exact', head: true })
      .is('vendor_id', null)
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { count, error } = await query;

    if (error) throw error;
    return asResponse(count ?? 0);
  },

  async getSpendByVendorOverTime({
    startDate,
    endDate,
    topN = 5,
  }: {
    startDate: string;
    endDate: string;
    topN?: number;
  }): Promise<
    AxiosResponse<{ months: string[]; vendors: Array<{ name: string; data: number[] }> }>
  > {
    let query = supabase
      .from('fx_transactions')
      .select('date, vendor_id, fx_vendors(name, display_name), amount')
      .eq('type', 'expense')
      .not('vendor_id', 'is', null)
      .gte('date', startDate)
      .lte('date', endDate);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    // Get top N vendors by total spend
    const vendorTotals: Record<string, { name: string; total: number }> = {};
    (data ?? []).forEach((row: any) => {
      const vId = row.vendor_id;
      if (!vendorTotals[vId]) {
        vendorTotals[vId] = {
          name: row.fx_vendors?.display_name || row.fx_vendors?.name || 'Unknown',
          total: 0,
        };
      }
      vendorTotals[vId].total += parseFloat(row.amount);
    });

    const topVendorIds = Object.entries(vendorTotals)
      .sort(([, a], [, b]) => b.total - a.total)
      .slice(0, topN)
      .map(([id]) => id);

    // Build month × vendor matrix
    const allMonths = new Set<string>();
    const vendorMonthly: Record<string, Record<string, number>> = {};

    (data ?? []).forEach((row: any) => {
      const month = row.date.substring(0, 7);
      allMonths.add(month);
      if (topVendorIds.includes(row.vendor_id)) {
        if (!vendorMonthly[row.vendor_id]) vendorMonthly[row.vendor_id] = {};
        vendorMonthly[row.vendor_id][month] =
          (vendorMonthly[row.vendor_id][month] ?? 0) + parseFloat(row.amount);
      }
    });

    const months = Array.from(allMonths).sort();
    const vendors = topVendorIds.map((vId) => ({
      name: vendorTotals[vId].name,
      data: months.map((m) => Math.round((vendorMonthly[vId]?.[m] ?? 0) * 100) / 100),
    }));

    return asResponse({ months, vendors });
  },
  // --- Review Queue methods ---

  async getReviewQueue({
    page,
    take,
    reviewStatus,
  }: {
    page: number;
    take: number;
    reviewStatus?: string;
  }): Promise<AxiosResponse<ResponseWithPagination<FxTransaction[]>>> {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('fx_transactions')
      .select(SELECT_WITH_JOINS, { count: 'exact' })
      .range(from, to)
      .order('category_id', { ascending: true, nullsFirst: true })
      .order('ai_confidence', { ascending: true, nullsFirst: true })
      .order('date', { ascending: false });

    query = applyOrgFilter(query);
    if (reviewStatus) {
      query = query.eq('review_status', reviewStatus);
    }

    const { data, error, count } = await query;
    if (error) throw error;

    return asResponse(
      buildPaginatedResponse((data ?? []).map(mapFxTransaction), count ?? 0, page, take)
    );
  },

  async updateReviewStatus(
    id: string,
    status: string
  ): Promise<AxiosResponse<FxTransaction>> {
    const updates: Record<string, unknown> = { review_status: status };
    if (status === 'approved') updates.user_verified = true;

    const result = await supabase
      .from('fx_transactions')
      .update(updates)
      .eq('id', id)
      .select(SELECT_WITH_JOINS)
      .single();

    return asResponse(mapFxTransaction(throwIfError(result)));
  },

  async bulkUpdateReviewStatus(ids: string[], status: string): Promise<void> {
    const updates: Record<string, unknown> = { review_status: status };
    if (status === 'approved') updates.user_verified = true;

    const { error } = await supabase
      .from('fx_transactions')
      .update(updates)
      .in('id', ids);

    if (error) throw error;
  },

  async approveHighConfidence(minConfidence = 0.9): Promise<number> {
    let query = supabase
      .from('fx_transactions')
      .update({ review_status: 'approved', user_verified: true })
      .eq('review_status', 'pending')
      .gte('ai_confidence', minConfidence)
      .not('vendor_id', 'is', null)
      .not('category_id', 'is', null)
      .select('id');
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return data?.length ?? 0;
  },

  async getReviewCounts(): Promise<{ pending: number; reviewed: number; approved: number }> {
    const counts = { pending: 0, reviewed: 0, approved: 0 };

    let pQuery = supabase.from('fx_transactions').select('id', { count: 'exact', head: true }).eq('review_status', 'pending');
    let rQuery = supabase.from('fx_transactions').select('id', { count: 'exact', head: true }).eq('review_status', 'reviewed');
    let aQuery = supabase.from('fx_transactions').select('id', { count: 'exact', head: true }).eq('review_status', 'approved');
    pQuery = applyOrgFilter(pQuery);
    rQuery = applyOrgFilter(rQuery);
    aQuery = applyOrgFilter(aQuery);

    const [p, r, a] = await Promise.all([pQuery, rQuery, aQuery]);

    counts.pending = p.count ?? 0;
    counts.reviewed = r.count ?? 0;
    counts.approved = a.count ?? 0;
    return counts;
  },

  async exportForCpa({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }): Promise<FxTransaction[]> {
    let query = supabase
      .from('fx_transactions')
      .select(SELECT_WITH_JOINS)
      .gte('date', startDate)
      .lte('date', endDate)
      .eq('review_status', 'approved')
      .order('date', { ascending: true });
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return (data ?? []).map(mapFxTransaction);
  },

  async hasAnyData(): Promise<boolean> {
    let query = supabase
      .from('fx_transactions')
      .select('id', { count: 'exact', head: true });

    query = applyOrgFilter(query);

    const { count, error } = await query.limit(1);
    if (error) throw error;
    return (count ?? 0) > 0;
  },
};

export default financeTransactionsService;
